
import React from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Icon, Tooltip } from '@dopomogai/ui';
import { motion } from 'framer-motion';

export interface CreditBalanceIndicatorProps {
  balance: number;
  isLoading: boolean;
  linkTo?: string;
  className?: string;
  variant?: 'default' | 'compact';
  tooltip?: string;
}

const CompactContent: React.FC<Pick<CreditBalanceIndicatorProps, 'balance' | 'isLoading'>> = ({
  balance,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-6 w-6">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-xs font-semibold text-slate-800">
      <Icon name="Wallet" className="h-3.5 w-3.5 text-slate-600" />
      {balance >= 100 ? null : <span>{balance.toFixed(0)}</span>}
    </div>
  );
};

const DefaultContent: React.FC<Pick<CreditBalanceIndicatorProps, 'balance' | 'isLoading'>> = ({
  balance,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center gap-2"
      >
        <Spinner size="sm" />
        <span className="text-sm font-medium text-slate-500">Loading...</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-2"
    >
      <Icon name="Wallet" className="h-5 w-5 text-slate-500" />
      <span className="text-sm font-semibold text-slate-800">
        {balance.toFixed(2)}
      </span>
      <span className="text-sm text-slate-600">Credits</span>
    </motion.div>
  );
};

/**
 * Displays the user’s credit balance with an optional compact mode.
 * The link defaults to `/app/buy-credits` but can be overridden.
 */
export const CreditBalanceIndicator: React.FC<CreditBalanceIndicatorProps> = ({
  balance,
  isLoading,
  linkTo = '/app/buy-credits',
  className,
  variant = 'default',
  tooltip = 'Click to top up your balance',
}) => {
  const content =
    variant === 'compact'
      ? <CompactContent balance={balance} isLoading={isLoading} />
      : <DefaultContent balance={balance} isLoading={isLoading} />;

  const baseClasses =
    variant === 'compact'
      ? 'flex items-center justify-center h-9 w-11 rounded-lg border-2 border-black bg-white transition-all duration-200 hover:-translate-x-px hover:-translate-y-px hover:shadow-[3px_3px_0_0_rgb(17,24,39)] active:translate-x-0 active:translate-y-0 active:shadow-none'
      : 'flex items-center justify-center h-10 px-4 rounded-lg bg-white border-2 border-black transform transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)] active:translate-x-0 active:translate-y-0 active:shadow-none';

  return (
    <Tooltip text={tooltip} position="bottom" disabled={isLoading}>
      <Link
        to={linkTo}
        aria-label="View and top up credit balance"
        className={`${baseClasses} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500 ${className ?? ''}`}
      >
        {content}
      </Link>
    </Tooltip>
  );
};